<template>
  <div> <div class="modal fade" ref="modalElement" id="comment-modal" tabindex="-1" aria-labelledby="commentModalLabel" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="commentModalLabel">{{ dialogTitle }}</h5>
          <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <form id="comment-form" class="p-3" @submit.prevent="submitComment">
            <div class="mb-3" v-if="!replyingToComment">
              <label id="rating-label" class="form-label d-block">Rating</label>
              <div class="star-rating" aria-labelledby="rating-label">
                <template v-for="n in 5" :key="n">
                  <input type="radio" class="btn-check" name="rating" :id="'rating' + n" :value="n" v-model="rating" :disabled="loading || uploading" autocomplete="off">
                  <label class="btn btn-outline-warning star-label"
                         :for="'rating' + n"
                         :class="{ 'filled': rating >= n || (hoveredRating >= n && hoveredRating > 0) }"
                         @mouseenter="hoveredRating = n"
                         @mouseleave="hoveredRating = 0">
                    <i class="material-icons">{{ (rating >= n || (hoveredRating >= n && hoveredRating > 0)) ? 'star' : 'star_border' }}</i>
                  </label>
                </template>
              </div>
            </div>

            <!-- Rating-based tags (mirrors the iOS/Android add-comment flow) -->
            <div class="mb-3" v-if="!replyingToComment && rating > 0">
              <label class="form-label d-block">Tags <span class="text-muted fw-normal">(optional)</span></label>
              <div class="rating-tags">
                <button
                  v-for="tag in quickTags"
                  :key="tag.key"
                  type="button"
                  class="badge card text-white comment-tag tag-chip"
                  :class="{ selected: selectedTags.includes(tag.key) }"
                  :disabled="loading || uploading"
                  @click="toggleTag(tag.key)">
                  {{ tag.label }}
                </button>
              </div>

              <template v-if="showAllTags">
                <div class="tag-section" v-for="section in expandedTagSections" :key="section.id">
                  <div class="tag-section-title">{{ section.title }}</div>
                  <div class="rating-tags">
                    <button
                      v-for="tag in section.tags"
                      :key="tag.key"
                      type="button"
                      class="badge card text-white comment-tag tag-chip"
                      :class="{ selected: selectedTags.includes(tag.key) }"
                      :disabled="loading || uploading"
                      @click="toggleTag(tag.key)">
                      {{ tag.label }}
                    </button>
                  </div>
                </div>
              </template>

              <button
                v-if="hasExpandedTags"
                type="button"
                class="btn btn-sm btn-link p-0 mt-1 tag-more-toggle"
                :disabled="loading || uploading"
                @click="showAllTags = !showAllTags">
                {{ showAllTags ? 'Less' : 'More' }}
              </button>
            </div>

            <div class="mb-3">
              <label for="input-2" class="form-label">Comment</label>
              <textarea
                class="form-control comment-textarea"
                id="input-2"
                v-model="body"
                :placeholder="commentPlaceholder"
                rows="3"
                style="max-height: calc(1.5em * 6 + 0.75rem + 2px); overflow-y: auto;"
              :disabled="loading || uploading"
              ></textarea>
            </div>

            <div class="mb-3">
              <label for="photosInput" class="form-label">Attach a photo</label>
              <input type="file"
                     class="form-control file-input"
                     id="photosInput"
                     multiple
                     :disabled="loading || uploading"
                     @change="fileInputEvent"
                     ref="photosInput"
                     accept="image/jpeg, image/png">
            </div>

            <div v-if="editingComment && editingComment.images && editingComment.images.length > 0" class="mb-3">
              <label class="form-label">Current Photos:</label>
              <div v-for="photo in editingComment.images" :key="photo.id" class="d-flex align-items-center mb-2">
                <img :src="photo.file.lgUrl"
                     alt="Comment photo"
                     style="width: 64px; height: 64px; object-fit: cover; object-position: center; margin-right: 10px; border: 1px solid #dee2e6;"/>
                <button @click.prevent="destroyPhoto(photo)" class="btn btn-sm btn-outline-danger d-flex align-items-center">
                  <span v-if="destroyingPhotoIds.includes(photo.id)" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  <span v-else class="material-icons me-1 fs-6">delete</span>
                  Delete
                </button>
              </div>
            </div>

            <div v-if="files && files.length > 0" class="mb-3">
              <label class="form-label">New Photos:</label>
              <div class="card row g-0 m-0 mb-2 py-2 px-2 align-items-center photo-upload-item" style="flex-direction: row;" v-for="file in files" :key="file.firebaseRefFullPath">
                <div class="col-5 d-flex align-items-center pe-2">
                  <small class="text-truncate" :title="file.name">{{ file.name }} - {{ Math.round(file.progress) }}%</small>
                </div>
                <div class="col-4 d-flex align-items-center justify-content-center px-2">
                  <div v-if="file.progress > 0 && file.progress < 100" class="progress w-100" style="height: 10px;">
                    <div class="progress-bar" role="progressbar" :style="{ width: file.progress + '%' }" :aria-valuenow="file.progress" aria-valuemin="0" aria-valuemax="100"></div>
                  </div>
                  <img v-if="file.photoUrl"
                       class="square"
                       alt="Uploaded preview"
                       :src="file.photoUrl"
                       style="width: 40px; height: 40px; object-fit: cover; border: 1px solid #dee2e6;"/>
                </div>
                <div class="col-3 d-flex align-items-center justify-content-end ps-2">
                  <button @click.prevent="removeFile(file)" class="btn btn-sm btn-secondary d-flex align-items-center">
                    <span class="material-icons fs-6">delete</span>
                  </button>
                </div>
              </div>
            </div>


            <div class="d-flex justify-content-end">
              <button id="resetButton"
                      type="button" class="btn btn-secondary me-2"
                      :disabled="loading || uploading"
                      @click="closeModal">
                Cancel
              </button>
              <button id="submit-button"
                      type="submit" class="btn btn-primary"
                      :disabled="loading || uploading"> <span v-if="loading" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                Submit
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { Modal } from 'bootstrap'; // Import Bootstrap's Modal component
import graph_client from '../../utils/graphql/graph_client';
import { GenerateDirectUpload } from '../../utils/graphql/comments-graph';
import { ratingTagSections } from '../../utils/models/RatingTag';
import SparkMD5 from 'spark-md5';

export default {
  // No BootstrapVue components needed
  data() {
    return {
      bootstrapModal: null, // To hold the Bootstrap Modal instance
      hoveredRating: 0, // Track which star is being hovered
      showAllTags: false, // Whether the expanded tag sections are revealed
    };
  },
  computed: {
    ...mapState(['editingComment', 'files', 'photoUrls', 'uploadedImages', 'destroyingPhotoIds', 'clientId', 'loading', 'uploading', 'replyingToComment', 'selectedTags']),
    // Rating-specific tag sections (quick picks + expandable sections).
    tagSections() {
      return ratingTagSections(this.rating);
    },
    quickTags() {
      const quick = this.tagSections.find((s) => s.id === 'quick');
      return quick ? quick.tags : [];
    },
    expandedTagSections() {
      return this.tagSections.filter((s) => s.id !== 'quick');
    },
    hasExpandedTags() {
      return this.expandedTagSections.length > 0;
    },
    body: {
      get() {
        return this.$store.state.body;
      },
      set(value) {
        this.$store.dispatch('setBody', value);
      },
    },
    rating: {
      get() {
        // Rating is now optional, can be null
        return this.$store.state.rating;
      },
      set(value) {
        // Ensure value passed is a number
        this.$store.dispatch('setRating', Number(value));
      },
    },
    showModal: {
      // This computed property now primarily *reads* the state
      get() {
        return this.$store.state.showModal;
      },
      // We don't define a setter here; we control visibility via watcher and methods
    },
    dialogTitle() {
      if (this.editingComment) {
        return 'Edit Comment';
      } else if (this.replyingToComment) {
        return `Reply to ${this.replyingToComment.user?.displayName || 'Comment'}`;
      }
      return 'Add Comment';
    },
    commentableName() {
      return this.$store.state.commentableType === 'Recording' ? 'recording' : 'hike';
    },
    commentPlaceholder() {
      return `Share your thoughts about this ${this.commentableName}...`;
    }
  },
  watch: {
    // Watch the Vuex state and show/hide the Bootstrap modal accordingly
    showModal(newValue) {
      if (!this.bootstrapModal) return;
      if (newValue) {
        this.bootstrapModal.show();
      } else {
        this.bootstrapModal.hide();
      }
    },
    // Collapse the expanded tag list whenever the rating changes, so the
    // newly-relevant quick picks are what the user sees first.
    rating() {
      this.showAllTags = false;
    },
  },
  mounted() {
    // Initialize the Bootstrap modal once the component is mounted
    const modalElement = this.$refs.modalElement;
    this.bootstrapModal = new Modal(modalElement);

    // Create bound event handler functions that can be removed later
    this.handleModalHidden = () => {
      if (this.showModal) { // Only update state if it was supposed to be open
        this.$store.dispatch('setShowModal', false);
      }
      // Reset file input when modal closes completely
      this.resetFileInput();
    };

    this.handleModalShow = () => {
      this.resetFileInput();
    };

    // Add event listeners
    modalElement.addEventListener('hidden.bs.modal', this.handleModalHidden);
    modalElement.addEventListener('show.bs.modal', this.handleModalShow);

    // If the modal should be shown immediately based on initial state
    if (this.showModal) {
      this.bootstrapModal.show();
    }
  },
  beforeUnmount() {
    // Clean up the modal instance and event listeners
    if (this.bootstrapModal) {
      const modalElement = this.$refs.modalElement;
      if (modalElement && this.handleModalHidden && this.handleModalShow) {
        // Remove event listeners only if element and handlers exist
        modalElement.removeEventListener('hidden.bs.modal', this.handleModalHidden);
        modalElement.removeEventListener('show.bs.modal', this.handleModalShow);
      }
      this.bootstrapModal.dispose(); // Dispose of the Bootstrap modal instance
    }
  },
  methods: {
    // handleModalHidden() { // Example if needing a named function for removal
    //   if (this.showModal) {
    //      this.$store.dispatch('setShowModal', false);
    //   }
    //   this.resetFileInput();
    // },
    resetFileInput() {
      const fileInput = this.$refs.photosInput;
      if (fileInput) {
        fileInput.value = '';
      }
    },
    destroyPhoto(photo, event) {
      // event.preventDefault(); // No longer needed if button type="button" or handled in @click.prevent
      this.$store.dispatch('destroyPhoto', { photo, commentId: this.editingComment.id });
    },
    removeFile(file, event) {
      // event.preventDefault(); // No longer needed if button type="button" or handled in @click.prevent
      this.$store.dispatch('removeFile', file);
    },
    toggleTag(tagKey) {
      this.$store.dispatch('toggleTag', tagKey);
    },
    submitComment() {
      // Basic validation: ensure body is not empty (rating is optional)
      this.$store.dispatch('submitComment');
      // Modal hiding should ideally be handled *after* successful submission
      // in the Vuex action or by watching a 'submissionSuccess' state.
      // For now, we assume the action might set showModal to false.
    },
    closeModal() {
      // Update the Vuex state, the watcher will handle hiding the modal
      this.$store.dispatch('setShowModal', false);
      // No need to call this.bootstrapModal.hide() directly due to the watcher
      // File input reset is now handled by the 'hidden.bs.modal' event listener
    },
    async calculateMD5Base64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const spark = new SparkMD5.ArrayBuffer();
          spark.append(e.target.result);
          const hash = spark.end();
          const bytes = new Uint8Array(hash.match(/.{2}/g).map(byte => parseInt(byte, 16)));
          resolve(btoa(String.fromCharCode(...bytes)));
        };
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
      });
    },

    async fileInputEvent() {
      const vm = this;
      const filesArray = Array.from(document.getElementById('photosInput').files);
      filesArray.forEach((f) => {
        f.progress = 0;
        vm.files.push(f);
      });

      for (const file of filesArray) {
        try {
          this.$store.dispatch('setUploading', true);

          // Calculate MD5 checksum
          const hashBase64 = await this.calculateMD5Base64(file);

          file.progress = 10; // Show some initial progress
          vm.$forceUpdate();

          // Step 1: Generate direct upload credentials
          const uploadResponse = await graph_client.request(GenerateDirectUpload, {
            input: {
              filename: file.name,
              byteSize: file.size,
              contentType: file.type || 'image/jpeg',
              checksumBase64: hashBase64,
              serviceName: window.publicBucketService // Specify the public bucket service
            }
          });

          const { uploadUrl, headers, signedId } = uploadResponse.generateDirectUpload;

          file.progress = 20;
          vm.$forceUpdate();

          // Step 2: Upload file directly to storage
          const uploadHeaders = JSON.parse(headers);
          console.log('Upload URL:', uploadUrl);
          console.log('Upload headers:', uploadHeaders);

          // For progress tracking, we'll simulate it with setTimeout
          const progressInterval = setInterval(() => {
            const currentFile = vm.files.find(f => f === file);
            if (currentFile && currentFile.progress < 85) {
              currentFile.progress += 5;
              vm.$forceUpdate();
            }
          }, 100);

          try {
            const uploadResponse = await fetch(uploadUrl, {
              method: 'PUT',
              headers: {
                ...uploadHeaders,
                'Content-Type': file.type || 'image/jpeg'
              },
              body: file
            });

            clearInterval(progressInterval);

            if (!uploadResponse.ok) {
              const errorText = await uploadResponse.text();
              console.error('Upload failed:', uploadResponse.status, errorText);
              throw new Error(`Upload failed: ${uploadResponse.statusText} - ${errorText}`);
            }
          } catch (error) {
            clearInterval(progressInterval);
            throw error;
          }

          file.progress = 90;
          vm.$forceUpdate();

          // Store the signed ID for later use in comment creation
          file.signedId = signedId;
          file.uploadUrl = uploadUrl; // Store for potential preview

          // For preview purposes, we can construct a temporary URL
          // Note: This might not work for all storage backends
          file.photoUrl = uploadUrl.split('?')[0]; // Basic URL without params
          vm.photoUrls.push(file.photoUrl);

          // Store signed ID in state for comment creation
          vm.$store.state.uploadedImages.push({
            firebaseImageReference: signedId,
            signedId: signedId
          });

          file.progress = 100;
          vm.$forceUpdate();

          if (vm.files.filter(f => f.progress === 100).length === vm.files.length) {
            this.$store.dispatch('setUploading', false);
            document.getElementById('photosInput').value = '';
          }
        } catch (error) {
          console.error('Upload error:', error);
          alert('Failed to upload image. Please try again.');
          // Remove failed file from list
          const index = vm.files.indexOf(file);
          if (index > -1) {
            vm.files.splice(index, 1);
          }
          this.$store.dispatch('setUploading', false);
        }
      }
    },
  },
};
</script>

<style lang="scss">

// Keep existing styles if they don't conflict heavily
// #comment-form { ... }
// #comment-modal .modal-header { ... } // Adjust selectors if needed

// Custom Star Rating Styles
.star-rating {
  display: inline-flex;
  gap: 0.25rem; // Add some spacing between stars
  margin-top: 0.5rem; // Add some top margin after the label
}

.star-label {
  font-size: 1.5rem; // Adjust size as needed (like size="lg")
  color: #adb5bd; // Default star color (empty)
  cursor: pointer;
  padding: 0 0.15em; // Adjust spacing
  transition: color 0.2s ease-in-out, transform 0.15s ease-in-out;
  border: none !important; // Remove default btn border
  background-color: transparent !important; // Remove default btn background
  box-shadow: none !important; // Remove default btn shadow
  line-height: 1; // Ensure icons align well

  // Override btn-outline-warning focus/active styles
  &:focus, &:active {
    box-shadow: none !important;
  }

  // Style the material icon inside the label
  .material-icons {
    color: #adb5bd; // Default star color (empty)
    font-size: 1.5rem;
  }
}

// Filled stars should be yellow
.star-label.filled .material-icons {
  color: #ffc107; // Filled star color (warning)
}

// Hover effect
.star-rating .star-label:hover {
  transform: scale(1.1); // Slightly reduced scale effect

  .material-icons {
    color: #ffca2c; // Slightly brighter hover color
  }
}

// Ensure filled state takes precedence
.star-label.filled .material-icons {
  color: #ffc107 !important; // Filled star color (warning)
}

// When disabled, reset cursor and reduce opacity
input.btn-check:disabled + .star-label {
  cursor: default;
  opacity: 0.65;
}


// Rating-based tag chips
.rating-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

// Shared sizing for the comment tags in both the add-comment modal and the
// comment threads (this style block is global, and both components ship in the
// same bundle). The bare Bootstrap `.badge` padding renders them too small.
.comment-tag {
  padding: 0.5rem 0.85rem;
  font-size: 0.85rem;
  line-height: 1.2;
}

// Base look comes from the shared `badge card text-white` classes so these
// match the tags rendered on comment threads elsewhere. This only layers on
// the interactive states the display tags don't need.
.tag-chip {
  cursor: pointer;
  transition: background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, color 0.15s ease-in-out, opacity 0.15s ease-in-out;

  &:hover:not(:disabled) {
    opacity: 0.85;
  }

  &.selected {
    // Selected == brand primary action (yellow w/ dark text), like .btn-primary.
    // !important overrides the `text-white` utility and `.card` background.
    background-color: var(--yellow, #fddf00) !important;
    border-color: var(--yellow, #fddf00) !important;
    color: var(--dark, #212529) !important;
  }

  &:disabled {
    opacity: 0.65;
    cursor: default;
  }
}

.tag-section {
  margin-top: 0.75rem;
}

.tag-section-title {
  font-size: 0.8rem;
  font-weight: 600;
  // Match the body-text color of the "Tags" form label above, rather than
  // the muted gray, so all tag headings in the modal read as one group.
  color: inherit;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.tag-more-toggle {
  text-decoration: none;
  font-size: 0.85rem;
}

// Ensure text area doesn't overflow modal badly
#input-2 {
  min-height: calc(1.5em * 3 + 0.75rem + 2px); // Corresponds to rows="3"
}

// Style for file preview cards
.card.row {
  border: 1px solid #dee2e6;
}

// Fix photo upload item layout
.photo-upload-item {
  overflow: hidden; // Prevent content from overflowing borders

  // Ensure columns have proper spacing and don't overflow
  .col-5, .col-4, .col-3 {
    min-width: 0; // Allow text truncation to work properly in flex containers
  }

  .text-truncate {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: block;
  }
}

.square {
  // Style for preview images if needed
}

// Ensure material icons align well with buttons
.material-icons.fs-6 {
  font-size: 1rem !important; // Match button text size better
  vertical-align: text-bottom;
}

// Fix white flash on file input and textarea - ensure proper text color
.comment-textarea,
.file-input {
  transition: none !important; // Prevent flash during value changes

  // Prevent color changes on disabled state
  &:disabled {
    opacity: 1 !important;
    background-color: #fff !important;
    color: #212529 !important;
  }
}

// Ensure file input maintains consistent styling
.file-input {
  &::-webkit-file-upload-button {
    transition: none !important;
  }

  // Prevent file input button from changing color when disabled
  &:disabled::-webkit-file-upload-button {
    opacity: 0.65;
  }
}

</style>
